/*
 * Reusable EventSource subscriber for `/api/events/stream`. Used by:
 *   - LiveMonitor (workspace-scoped) — `/app/live`
 *   - LiveMonitor (cross-tenant)     — `/admin/live`         (scope=platform)
 *   - LivePanel (inline on per-domain pages, e.g. Datasets, Metadata browser)
 *
 * The hook owns the EventSource lifecycle: opens on mount, closes on
 * unmount, exposes the connection state, the rolling tail (capped),
 * counter computation across fixed time windows, pause/resume, and
 * client-side category filtering.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { loadSession } from "./api.js";

// Mirrors services/tallystream-api/src/storage/eventBus.ts. The legacy
// "data" alias is kept for one release so old emitters keep flowing
// through; new pages should subscribe to "trk-data" or "agg-data".
export type LiveEventCategory =
  | "metadata"
  | "trk-data"
  | "agg-data"
  | "data"
  | "tally"
  | "aggregate"
  | "indicator"
  | "job"
  | "dlq";

export interface LiveEvent {
  id: string;
  ts: string;
  workspaceId?: string;
  category: LiveEventCategory;
  action: string;
  detail: Record<string, unknown>;
}

export type LiveConnectionState = "connecting" | "open" | "closed";

export interface UseLiveEventsOptions {
  /** "platform" for cross-tenant, otherwise omitted — server picks workspace from JWT. */
  scope?: "platform";
  /** Drop events whose category isn't in this list (server still ships them; we filter client-side). */
  categories?: LiveEventCategory[];
  /** Drop events whose action isn't matched by this prefix (e.g. "aggregate-metadata-sync"). */
  actionPrefix?: string;
  /** Custom predicate; return false to drop the event. Runs after category + actionPrefix gates. */
  filter?: (event: LiveEvent) => boolean;
  /** Hard cap on the in-memory tail. Default 200. */
  tailMax?: number;
  /** When true, paused — incoming events are dropped, connection stays open. */
  paused?: boolean;
}

export interface UseLiveEventsResult {
  events: LiveEvent[];
  connectionState: LiveConnectionState;
  clear: () => void;
  countersIn: (windowMs: number) => { total: number; byCategory: Record<LiveEventCategory, number> };
}

const ALL_CATEGORIES: LiveEventCategory[] = ["metadata", "trk-data", "agg-data", "data", "tally", "aggregate", "indicator", "job", "dlq"];

export function useLiveEvents(opts: UseLiveEventsOptions = {}): UseLiveEventsResult {
  const tailMax = opts.tailMax ?? 200;
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [connectionState, setConnectionState] = useState<LiveConnectionState>("connecting");
  const pausedRef = useRef(opts.paused ?? false);
  pausedRef.current = opts.paused ?? false;
  const acceptCategories = opts.categories;
  const acceptCategoriesSet = useMemo(
    () => acceptCategories ? new Set(acceptCategories) : null,
    [acceptCategories?.join(",")] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const actionPrefix = opts.actionPrefix;
  const customFilter = opts.filter;
  const customFilterRef = useRef(customFilter);
  customFilterRef.current = customFilter;

  useEffect(() => {
    const session = loadSession();
    if (!session) return;
    const params = new URLSearchParams({ token: session.token });
    if (opts.scope) params.set("scope", opts.scope);
    const url = `/api/events/stream?${params.toString()}`;
    const es = new EventSource(url);
    es.addEventListener("open", () => setConnectionState("open"));
    es.addEventListener("error", () => setConnectionState("closed"));
    const onMessage = (e: MessageEvent) => {
      if (pausedRef.current) return;
      try {
        const event = JSON.parse(e.data) as LiveEvent;
        if (acceptCategoriesSet && !acceptCategoriesSet.has(event.category)) return;
        if (actionPrefix && !event.action.startsWith(actionPrefix)) return;
        if (customFilterRef.current && !customFilterRef.current(event)) return;
        setEvents((prev) => {
          const next = [event, ...prev];
          if (next.length > tailMax) next.length = tailMax;
          return next;
        });
      } catch { /* swallow malformed */ }
    };
    for (const c of ALL_CATEGORIES) es.addEventListener(c, onMessage as EventListener);
    es.onmessage = onMessage;
    return () => { es.close(); };
    // Re-open the EventSource only when scope changes; category/action
    // filters are applied in-memory so swapping them shouldn't reconnect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.scope]);

  const clear = () => setEvents([]);

  const countersIn = (windowMs: number) => {
    const cutoff = Date.now() - windowMs;
    const byCategory: Record<LiveEventCategory, number> = {
      metadata: 0, "trk-data": 0, "agg-data": 0, data: 0, tally: 0, aggregate: 0, indicator: 0, job: 0, dlq: 0
    };
    let total = 0;
    for (const e of events) {
      const t = Date.parse(e.ts);
      if (!Number.isFinite(t) || t < cutoff) continue;
      total += 1;
      byCategory[e.category] += 1;
    }
    return { total, byCategory };
  };

  return { events, connectionState, clear, countersIn };
}

export const LIVE_CATEGORIES: Array<{ key: LiveEventCategory; label: string; color: string }> = [
  { key: "metadata",  label: "Metadata",        color: "#0E8A80" },
  { key: "trk-data",  label: "Tracker data",    color: "#0B2545" },
  { key: "agg-data",  label: "Aggregate data",  color: "#0E8A50" },
  { key: "data",      label: "Data (legacy)",   color: "#0B2545" },
  { key: "tally",     label: "Tally",           color: "#7A4A00" },
  { key: "aggregate", label: "Aggregate engine", color: "#0E8A50" },
  { key: "indicator", label: "Indicator",       color: "#5E1F8E" },
  { key: "job",       label: "Job",             color: "#475569" },
  { key: "dlq",       label: "Dead-letter",     color: "#C03030" }
];

export function summarizeLiveDetail(d: Record<string, unknown>): string {
  const interesting = ["jobId", "type", "result", "message", "attempts", "version", "dataSetCount", "ingested", "rollupRunIds", "programCount", "items"];
  const parts: string[] = [];
  for (const k of interesting) {
    if (k in d) {
      const v = d[k];
      if (v == null) continue;
      if (typeof v === "object") {
        const s = JSON.stringify(v);
        if (s.length <= 100) parts.push(`${k}=${s}`);
      } else {
        parts.push(`${k}=${String(v)}`);
      }
    }
  }
  return parts.join(" · ");
}

export function fmtLiveTime(iso: string): string {
  try { return new Date(iso).toLocaleTimeString(); } catch { return iso; }
}
